import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { sortBy } from 'lodash';
import { LocalStorageService } from 'projects/ppb-shared-library/src/lib/services/local-storage.service';
import { BehaviorSubject, forkJoin, Observable, from } from 'rxjs';
import { AccountIalEnum } from '../models/account.model';
import {
  DictionaryCategory,
  DictionaryModel,
  DictionaryModelKey,
} from '../models/dictionary.model';
import { DictionaryService } from './dictionary.service';
import { SharedApiService } from './interfaces/shared-api.service';

/**
 * Used in app.module.ts to load dictionary when app is initialized
 *
 * @param provider Dictionary service
 */
export function dictionary2ProviderFactory(
  provider: MultiLangDictService
): () => Promise<DictionaryModel[]> {
  return () => provider.load();
}

export const USE_MULTI_LANG_DICT = new InjectionToken<boolean>(
  'should use MultiLangDictService'
);

@Injectable({
  providedIn: 'root',
})
export class MultiLangDictService {
  private dictionary = new BehaviorSubject<DictionaryModelKey>(null);
  private dictionaryPl = 'assets/dictionary_pl.json';
  private dictionaryEn = 'assets/dictionary_en.json';
  private readonly isDefaultLangUse: boolean = false;

  constructor(
    @Optional() @Inject(USE_MULTI_LANG_DICT) private useMultiLangDict: boolean,
    private api: SharedApiService,
    private localStorageService: LocalStorageService,
    private oldDictionaryService: DictionaryService
  ) {
    this.isDefaultLangUse = this.useMultiLangDict || this.isDefaultLangUse;
  }

  /** Used by factory to get dictionary when app is initialized */
  load(): Promise<DictionaryModel[]> {
    if (!this.useMultiLangDict) {
      return this.oldDictionaryService.load();
    }

    return new Promise((resolve) => {
      forkJoin({
        oldDictionary: from(this.oldDictionaryService.load()),
        newDictionary: this.getDictionary(),
      }).subscribe(({ oldDictionary, newDictionary }) => {
        const oldMapped = (oldDictionary as []).reduce(
          (r: DictionaryModel, a: DictionaryModel) => {
            r[a.type] = r[a.type] || [];
            const fromNewDictionary = newDictionary[
              a.type as unknown as DictionaryCategory
            ]?.find((x) => x.id === a.id);
            if (!fromNewDictionary) {
              return r;
            }
            a.name = fromNewDictionary.name;
            a.applicationName = fromNewDictionary.applicationName;
            r[a.type].push(a);
            return r;
          },
          Object.create(null)
        );
        this.dictionary.next(oldMapped);
        resolve(oldMapped);
      });
    });
  }

  getDictionary(): Observable<DictionaryModelKey> {
    return this.localStorageService.getPreferredLanguage() === 'en'
      ? this.api.getPublic(this.dictionaryEn)
      : this.api.getPublic(this.dictionaryPl);
  }

  getSecurityLevels(): DictionaryModel[] {
    return this.getCollectionFromDictionary(
      DictionaryCategory.SECURITY_LEVEL
    ).sort((a, b) =>
      a.id === AccountIalEnum.HIGH || b.id === AccountIalEnum.LOW
        ? 1
        : a.id === AccountIalEnum.LOW || b.id === AccountIalEnum.HIGH
        ? -1
        : 0
    );
  }

  getNameFromDictionary(name: string): string {
    let singleRow: DictionaryModel;
    Object.keys(this.dictionary.getValue()).forEach((dict) => {
      const singleRowForCategory = (this.dictionary.getValue() as any)[
        dict
      ].find((d: { id: string; name: string }) => d.id === name);
      if (singleRowForCategory !== undefined) {
        singleRow = singleRowForCategory;
      }
    });
    return singleRow ? singleRow.name : name;
  }

  getNameFromExistedStream(name: string): string {
    return this.getNameFromDictionary(name);
  }

  getApplicationNameFromDictionary(applicationName: string): string {
    let singleRow: DictionaryModel;
    Object.keys(this.dictionary.getValue()).forEach((dict) => {
      const singleRowForCategory = (this.dictionary.getValue() as any)[
        dict
      ].find((d: { id: string; applicationName: string }) => d.id === applicationName);
      if (singleRowForCategory !== undefined) {
        singleRow = singleRowForCategory;
      }
    });
    return singleRow ? singleRow.applicationName : applicationName;
  }
  
  getApplicationNameFromExistedStream(applicationName: string): string {
    return this.getApplicationNameFromDictionary(applicationName);
  }

  getCollectionFromDictionarySortedByName(
    category: DictionaryCategory
  ): DictionaryModel[] {
    return this.dictionary.getValue()[category].sort((x, y) => {
      return x.name.localeCompare(y.name);
    });
  }

  getSortedByName(category: DictionaryCategory): DictionaryModel[] {
    return this.getCollectionFromDictionarySortedByName(category);
  }

  private getCollectionFromDictionary(
    category: DictionaryCategory
  ): DictionaryModel[] {
    return this.dictionary.getValue()[category];
  }
}
